import { useState, useEffect, useCallback } from 'react';

export const useDashboardMetrics = (tenantId = null, autoRefresh = true, refreshInterval = 30000) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [currentTenantId, setCurrentTenantId] = useState(tenantId || localStorage.getItem('tenantId'));

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const effectiveTenantId = tenantId || currentTenantId || localStorage.getItem('tenantId');
      const url = (effectiveTenantId && effectiveTenantId !== 'ALL') 
        ? `${process.env.REACT_APP_BACKEND_URL}synctrack/dashboard/metrics/tenant/${effectiveTenantId}`
        : `${process.env.REACT_APP_BACKEND_URL}synctrack/dashboard/metrics`;

      console.log('📊 Fetching dashboard metrics with URL:', url);
      console.log('🆔 Effective tenant ID:', effectiveTenantId);

      const response = await fetch(url, { headers });
      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setMetrics(data);
        setLastRefresh(new Date());
      } else {
        throw new Error(data.error || 'Failed to fetch metrics');
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, currentTenantId]);

  // Initial fetch
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchMetrics();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchMetrics, refreshInterval]);

  // Listen for tenant changes
  useEffect(() => {
    const handleTenantChange = (event) => {
      const newTenantId = event.detail?.tenantId?.toString();
      if (newTenantId && newTenantId !== currentTenantId) {
        console.log('🔄 Tenant changed in metrics hook, updating from', currentTenantId, 'to', newTenantId);
        setCurrentTenantId(newTenantId);
        // Fetch metrics immediately for new tenant
        fetchMetrics();
      }
    };
    
    window.addEventListener('tenantChange', handleTenantChange);
    
    return () => {
      window.removeEventListener('tenantChange', handleTenantChange);
    };
  }, [currentTenantId, fetchMetrics]);

  const refresh = useCallback(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    loading,
    error,
    lastRefresh,
    refresh
  };
};

export default useDashboardMetrics;
